// Wave 20.8 — Web identity gate.
//
// Wraps authenticated routes. When the logged-in user has no
// username OR no display name, renders a blocking modal setup form
// instead of the underlying page. Public routes (login, register,
// magic-link, /shared/, /peak/, etc.) are skipped so the guard
// doesn't loop or block sign-in.
//
// Fetches /users once per mount. If the fetch fails, we fail
// OPEN — the page renders normally — since a broken profile
// endpoint shouldn't lock every user out.

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { colors } from '../theme';

const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';
const API = process.env.REACT_APP_API || 'https://api.aero.chat';

// Paths that never gate on identity — public URLs, auth flows, and
// the setup screen itself. Kept in sync with RaphaGuard's list.
const SKIP_PREFIXES = [
  '/login',
  '/register',
  '/password',
  '/verify',
  '/privacy',
  '/terms',
  '/liability',
  '/rapha/register',
  '/rapha/welcome',
  '/shared/',
  '/peak/',
  '/dropped/',
  '/drops',
  '/u/',
];

interface Profile {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  displayName?: string;
}

interface Props {
  children: React.ReactNode;
}

export default function IdentityGuard({ children }: Props) {
  const { pathname } = useLocation();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoaded(true);
      return;
    }
    let cancelled = false;
    fetch(`${API}/users`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => {
        if (!cancelled) {
          setProfile(u);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setLoaded(true); // fail open
      });
    return () => {
      cancelled = true;
    };
    // Re-check on route change so a token added mid-session (e.g. via
    // login) triggers the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const onPublic = SKIP_PREFIXES.some((p) => pathname.startsWith(p));
  const needsIdentity =
    !onPublic &&
    !!profile &&
    (!profile.username || !profile.displayName || !profile.displayName.trim());

  if (!loaded) return <>{children}</>;

  // Wave 20.24 — When gating, don't render children behind the modal.
  // Same rationale as LiabilityGuard: single-modal-at-a-time + no
  // underlying-page leak through the backdrop.
  if (needsIdentity && profile) {
    return <IdentitySetupModal profile={profile} onDone={(u) => setProfile(u)} />;
  }
  return <>{children}</>;
}

function IdentitySetupModal({
  profile,
  onDone,
}: {
  profile: Profile;
  onDone: (updated: Profile) => void;
}) {
  const [username, setUsername] = useState(profile.username || '');
  const [displayName, setDisplayName] = useState(
    profile.displayName ||
      [profile.firstName, profile.lastName].filter(Boolean).join(' ') ||
      '',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit =
    /^[a-z0-9_-]{3,30}$/.test(username) && displayName.trim().length > 0;

  const submit = async () => {
    if (!canSubmit) {
      setError('Enter a valid username (3–30 chars, a-z 0-9 _ -) and a display name.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/users/me/profile`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, displayName: displayName.trim() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || `HTTP ${res.status}`);
      }
      const updated = await res.json();
      onDone(updated);
    } catch (e: any) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      disableEscapeKeyDown
      hideBackdrop={false}
      PaperProps={{
        sx: {
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 3,
          p: 3,
          maxWidth: 460,
          width: '100%',
        },
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
        Pick a username
      </Typography>
      <Typography sx={{ color: colors.textMuted, mb: 3, fontSize: '0.9rem' }}>
        Your username is how you log in and how friends recognize your
        Drop Links. Your display name is what shows on shared pages.
      </Typography>
      <Stack spacing={2}>
        <TextField
          label="Username"
          value={username}
          onChange={(e) =>
            setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))
          }
          inputProps={{ maxLength: 30 }}
          helperText="3–30 chars, lowercase letters, digits, _ or -."
          autoFocus
          fullWidth
        />
        <TextField
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          inputProps={{ maxLength: 80 }}
          helperText="Shown on public Drop Link pages."
          fullWidth
        />
        {error && <Alert severity="error">{error}</Alert>}
        <Button
          variant="contained"
          onClick={submit}
          disabled={saving || !canSubmit}
          sx={{
            backgroundColor: colors.brand,
            color: colors.textInverse,
            fontWeight: 800,
            py: 1.25,
          }}
        >
          {saving ? <CircularProgress size={20} /> : 'Save + continue'}
        </Button>
      </Stack>
    </Dialog>
  );
}
